import React, { Fragment } from "react";
import PropTypes from "prop-types";
import debounce from "lodash.debounce";

import "./CommentsPage.css";
import mentionsClass from "./mentions.module.css";
import { TOPIC } from "App/Routes";
import Link from "components/shared/Link/Link";
import Comment from "./Comment";
import AchievementsModal from "components/shared/Achievements/AchievementsModal/AchievementsModal";
import { MentionsInput, Mention } from "react-mentions";
import CharacterCounter from "components/shared/Inputs/CharacterCounter";

import localize from "lang/localize";
import getApiGenerator from "services/getApiGenerator";
import { SEARCH_PROJECT } from "services/api";
import { MENTIONS_LIMIT } from "config";
import Loading from "components/shared/Loading";

const propTypes = {
  isLoadingMore: PropTypes.bool,
  comments: PropTypes.array,
  more: PropTypes.bool,
  showInput: PropTypes.bool,
  input: PropTypes.string,
  handleMore: PropTypes.func,
  handleInputChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  id: PropTypes.string,
  type: PropTypes.string,
  language: PropTypes.string,
  projectId: PropTypes.number,
  userId: PropTypes.number,
  removeComment: PropTypes.func,
  /* for switching to Achievements modal */
  showAchievements: PropTypes.bool,
  achievements: PropTypes.array,
  setHideAchievements: PropTypes.func,
  setAchievements: PropTypes.func,
  // character limit
  charLimit: PropTypes.number
};

const ChallengesCommentsNav = props => {
  if (props.type === "quest") {
    return (
      <div className="container verticalpadding">
        <nav className="nav-buttons">
          <Link to={TOPIC.format(props.id)} className="button inactive">
            <span className="capitalize">
              <i className="fas fa-th-list"></i>
              {localize("challenges_novariable_text", props.language)
                .format("")
                .trim()}
            </span>
          </Link>
          <button className="button active">
            <span className="capitalize">
              <i className="fas fa-comment"></i>
              {localize("comments_novariable_text", props.language)
                .format("")
                .trim()}
            </span>
          </button>
        </nav>
      </div>
    );
  } else {
    return null;
  }
};

const Comments = props => {
  if (
    props.comments &&
    Array.isArray(props.comments) &&
    props.comments.length > 0
  ) {
    return (
      <div className="topmargin-30">
        {props.comments.map((comment, index) => (
          <Fragment key={comment.id}>
            <Comment
              id={comment.id}
              commentUserName={comment.userName}
              commentUserPhoto={comment.photoSmall}
              commentUserId={comment.userId}
              userId={props.userId}
              userRankStatus={comment.userRankStatus}
              userRankStatusImage={comment.userRankStatusImage}
              message={comment.message}
              messageAttributed={comment.commentMessageAttributed}
              createdAtFormatted={comment.createdAtFormatted}
              projectId={props.projectId}
              removeComment={props.removeComment}
              type={props.type}
              typeId={props.id}
              commentThreadCount={comment.commentThreadCount}
            />
          </Fragment>
        ))}
      </div>
    );
  } else {
    return (
      <p className="topmargin-30 text-align-center">
        {localize("empty_comment_table_text", props.language)}
      </p>
    );
  }
};

const CommentInput = props => {
  const [mentionsUserList, setMentionsUserList] = React.useState([]);
  const [storedUserList, setStoredUserList] = React.useState([]);
  const [numberOfMentions, setNumberOfMentions] = React.useState(0);

  const getPlayers = debounce((search, callback) => {
    getApiGenerator(
      SEARCH_PROJECT.format(props.projectId),
      {
        type: "user",
        keywords: search
      },
      props.sessionKey
    ).end((err, res) => {
      if (err || res.body.code !== 200) {
        if (res.body.code === 500) {
          return [];
        }
      } else {
        const formattedPlayers = res.body.data.map(user => ({
          id: user.id,
          display: user.userName ? user.userName : " ",
          userImage: user.userImage
        }));
        setMentionsUserList(formattedPlayers);
        callback(formattedPlayers);
      }
    });
  }, 500);

  const onAdd = search => {
    const foundUser = mentionsUserList.find(
      user => user.id === parseInt(search)
    );
    setStoredUserList([...storedUserList, foundUser]);
  };

  const resetNumberOfMentions = () => {
    setNumberOfMentions(0);
  };

  // checks input against regex to count accurate number of mentions
  const checkInput = event => {
    const regexp = /(\[uid:[0-9]+\])/gm;
    let mentionsInInput = event.target.value.match(regexp);
    if (mentionsInInput !== null) {
      setNumberOfMentions(event.target.value.match(regexp).length);
    } else {
      setNumberOfMentions(0);
    }
    props.handleInputChange(event);
  };

  if (!!props.showInput) {
    const transformedInput = props.input.replace(/(\[uid:[0-9]+\])/gm, "");
    return (
      <div id="commentInput" className="bottompadding">
        <form className="pure-form" onSubmit={props.handleSubmit}>
          <div className="pure-u-1 relative">
            <MentionsInput
              value={props.input}
              placeholder={localize("comment_placeholder_text", props.language)}
              onChange={checkInput}
              classNames={mentionsClass}
              allowSpaceInQuery={true}
              a11ySuggestionsListLabel="Suggested mentions"
            >
              <Mention
                trigger="@"
                data={getPlayers}
                renderSuggestion={(suggestion, search, highlightedDisplay) => (
                  <div className="flex align-items-center">
                    <div className="rightmargin-20 suggestion-image-container">
                      <img
                        className="suggestion-image"
                        src={suggestion.userImage}
                        alt="pfp"
                      />
                    </div>
                    {suggestion.display}
                  </div>
                )}
                displayTransform={id => {
                  const foundUser = mentionsUserList.find(
                    user => user.id === parseInt(id)
                  );
                  const storedUser = storedUserList.find(
                    user => user.id === parseInt(id)
                  );
                  return foundUser
                    ? `@${foundUser.display}`
                    : `@${storedUser.display}`;
                }}
                onAdd={onAdd}
                markup="[uid:__id__]"
                className={mentionsClass.mentions__mention}
              />
            </MentionsInput>
            <CharacterCounter charLimit={props.charLimit} input={props.input} />
          </div>
          <div className="pure-u-1">
            {numberOfMentions > MENTIONS_LIMIT && (
              <span className="text-red">
                {localize(
                  "alert_exceeded_mentions_limit",
                  props.language
                ).format(MENTIONS_LIMIT)}
              </span>
            )}
            <button
              type="submit"
              onClick={resetNumberOfMentions}
              disabled={
                props.input.trim() === "" ||
                numberOfMentions > MENTIONS_LIMIT ||
                (props.charLimit !== 0 &&
                  transformedInput.length > props.charLimit)
                  ? true
                  : false
              }
              className={
                "button nomargin floatright " +
                (props.input.trim() === "" ||
                numberOfMentions > MENTIONS_LIMIT ||
                (props.charLimit !== 0 &&
                  transformedInput.length > props.charLimit)
                  ? "disabled"
                  : "cta")
              }
            >
              {localize("nav_bar_title_comments_text", props.language)}
            </button>
          </div>
        </form>
      </div>
    );
  } else {
    return null;
  }
};

// const MoreButtonContainer = props => {
//   if (props.more) {
//     return (
//       <div className="more-container flex justify-content-center">
//         <button id="pagination" className="button" onClick={props.handleMore}>
//           {localize("more_text", props.language)}
//         </button>
//       </div>
//     );
//   } else {
//     return null;
//   }
// };

const CommentsPage = props => {
  return (
    <Fragment>
      <ChallengesCommentsNav
        id={props.id}
        type={props.type}
        language={props.language}
      />
      <div className="comments-container container verticalpadding bottompadding-floating-extended">
        <div className="innerblock">
          <CommentInput
            projectId={props.projectId}
            sessionKey={props.sessionKey}
            showInput={props.showInput}
            input={props.input}
            handleSubmit={props.handleSubmit}
            handleInputChange={props.handleInputChange}
            language={props.language}
            charLimit={props.charLimit}
          />
          <Comments
            comments={props.comments}
            userId={props.userId}
            projectId={props.projectId}
            removeComment={props.removeComment}
            type={props.type}
            id={props.id}
            language={props.language}
          />
          <AchievementsModal
            showModal={props.showAchievements}
            handleCloseModal={props.setHideAchievements}
            achievements={props.achievements}
          />
          {props.isLoadingMore ? <Loading /> : null}
          {/* <MoreButtonContainer
            more={props.more}
            handleMore={props.handleMore}
            language={props.language}
          /> */}
        </div>
      </div>
    </Fragment>
  );
};

CommentsPage.propTypes = propTypes;

export default CommentsPage;
